import React, { useState, useEffect } from "react";
import axios from "axios";
import ResponsiveModalAbsolute from "../../../components/ResponsiveModalAbsolute";
import useHeaders from "../../../hooks/useHeaders";
import Spinner from "components/helpers/Spinner";

// Definimos los posibles estados (ENUM) para consistencia con el backend
enum EstadoLimpieza {
    LIMPIA = "Limpia",
    SUCIA = "Sucia",
    LIMPIANDO = "Limpiando",
}

interface Habitacion {
    id: number;
    nombre: string;
    estado_limpieza: EstadoLimpieza; // <--- Campo necesario del backend
    banner?: string;
}

interface Props {
    modalVisible: boolean;
    setModalVisible: (b: boolean) => void;
    idHotel: number;
    onSelect: (h: Habitacion | null) => void;
}

export const ModalSeleccionarHabitacion = ({
    modalVisible,
    setModalVisible,
    idHotel,
    onSelect,
}: Props) => {
    const { headers } = useHeaders();
    const [habitaciones, setHabitaciones] = useState<Habitacion[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (modalVisible) {
            setLoading(true);
            axios
                .get(
                    `${process.env.REACT_APP_PROXY}/hotel_habitaciones/getAllByHotel/${idHotel}?query='Publicado'`,
                    { headers },
                )
                .then((res) => setHabitaciones(res.data))
                .finally(() => setLoading(false));
        }
    }, [modalVisible, idHotel]);

    // Función helper para obtener los colores del badge según el estado
    const getBadgeClass = (estado: EstadoLimpieza) => {
        switch (estado) {
            case EstadoLimpieza.LIMPIA:
                return "bg-green-100 text-green-700 border-green-300";
            case EstadoLimpieza.SUCIA:
                return "bg-red-100 text-red-700 border-red-300";
            case EstadoLimpieza.LIMPIANDO:
                return "bg-yellow-100 text-yellow-700 border-yellow-300";
            default:
                return "bg-gray-100 text-gray-700 border-gray-300";
        }
    };

    return (
        <ResponsiveModalAbsolute
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
        >
            <div className="p-6 max-w-4xl mx-auto">
                <div className="text-center mb-6">
                    <h2 className="text-xl font-bold text-gray-800">
                        Seleccionar Habitación
                    </h2>
                    <p className="text-sm text-gray-500">
                        Haz clic en una habitación para filtrar el listado
                    </p>
                </div>

                {loading ? (
                    <div className="text-center py-10">
                        <Spinner />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 overflow-y-auto max-h-[60vh] p-2">
                        {habitaciones.map((h) => (
                            <div
                                key={h.id}
                                onClick={() => {
                                    onSelect(h);
                                    setModalVisible(false);
                                }}
                                className="cursor-pointer border-2 border-gray-100 rounded-xl overflow-hidden hover:shadow-2xl hover:border-blue-500 transition-all group relative bg-white"
                            >
                                {/* --- EL BADGE (Posicionamiento absoluto) --- */}
                                <div
                                    className={`absolute top-2 right-2 px-3 py-1 text-xs font-bold rounded-full border shadow-sm z-10 ${getBadgeClass(h.estado_limpieza)}`}
                                >
                                    {h.estado_limpieza}
                                </div>

                                <div className="overflow-hidden">
                                    <img
                                        src={
                                            `${process.env.REACT_APP_PROXY}/hotel_habitaciones/getImage/` +
                                            h?.banner
                                        }
                                        alt={h.nombre}
                                        className="w-full h-36 object-cover group-hover:scale-110 transition-transform duration-300"
                                    />
                                </div>
                                <div className="p-4 text-center border-t">
                                    <p className="font-semibold text-gray-800">
                                        {h.nombre}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        ID: {h.id}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {habitaciones.length === 0 && !loading && (
                    <div className="text-center py-10 text-gray-500 border-2 border-dashed rounded-lg">
                        No hay habitaciones registradas en este hotel.
                    </div>
                )}

                <button
                    onClick={() => {
                        onSelect(null);
                        setModalVisible(false);
                    }}
                    className="w-full mt-8 p-3 text-red-600 font-bold border-2 border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                    Quitar filtro / Ver Todas las habitaciones
                </button>
            </div>
        </ResponsiveModalAbsolute>
    );
};
